import { Button, Icon, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { packages, type Package } from "../../api/packages.service";
import { PackageCreateModal } from "../../components/PackageCreateModal";
import { PageHeader } from "../../components/PageHeader";
import { UnauthorizedView } from "../../components/UnauthorizedView";
import PermissionGate from "../../components/Permissions/PermissionGate";
import PermissionBanner from "../../components/Permissions/PermissionBanner";
import { ROUTES } from "../../routes";
import styles from "./Packages.module.css";

function PackagesLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [authError, setAuthError] = useState<Error | null>(null);
  const [_authCheckLoading, setAuthCheckLoading] = useState(true);
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
  } | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);

  // Check for auth errors on mount
  useEffect(() => {
    async function checkAuth() {
      try {
        // Make a lightweight request to check auth
        await packages.getAll({ limit: 1, offset: 0 });
        setAuthCheckLoading(false);
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to check auth");
        const status = (error as Error & { status?: number }).status;
        const isAuthError = status === 401 || status === 403;

        if (isAuthError) {
          setAuthError(error);
        }
        setAuthCheckLoading(false);
      }
    }
    checkAuth();
  }, []);

  function handleOpenCreateModal() {
    setIsCreateModalOpen(true);
  }

  function handleCloseCreateModal() {
    setIsCreateModalOpen(false);
  }

  function handleCreateSuccess(pkg: Package) {
    setIsCreateModalOpen(false);
    setToast({
      message: `Package "${pkg.productCode}" created successfully`,
      type: "success",
    });

    const isOnDetailPage = location.pathname !== ROUTES.fulfilment.packages;

    if (isOnDetailPage) {
      navigate(ROUTES.fulfilment.packageDetail(pkg.id));
    } else {
      setRefetchTrigger(prev => prev + 1);
    }
  }

  function handleCreateError(error: Error) {
    const errorWithDetails = error as Error & {
      details?: Array<{ path: (string | number)[]; message: string }>;
    };
    let message = `Failed to create package: ${error.message}`;

    if (errorWithDetails.details?.length) {
      const fieldErrors = errorWithDetails.details
        .map(d => `${d.path.join(".")}: ${d.message}`)
        .join(", ");
      message = `Failed to create package: ${fieldErrors}`;
    }

    setToast({
      message,
      type: "error",
    });
  }

  function handleCloseToast() {
    setToast(null);
  }

  function handleMutationSuccess() {
    setRefetchTrigger(prev => prev + 1);
  }

  function handleBackClick() {
    if (window.history.length > 1 && location.key !== "default") {
      navigate(-1);
    } else {
      navigate(ROUTES.fulfilment.packages);
    }
  }

  const isDetailPage = location.pathname !== ROUTES.fulfilment.packages;

  // Show unauthorized view if auth check failed
  if (authError) {
    return <UnauthorizedView />;
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader title="Packages" subtitle="Manage product packages and feature bundles">
          <div style={{ display: "flex", gap: "12px" }}>
            {isDetailPage && !isEditMode && (
              <Button variant="outlined" color="default" onClick={handleBackClick}>
                <Icon icon="arrow-left" size="small" />
                Back
              </Button>
            )}
            <PermissionGate permission="fulfilment.create_package">
              {!isEditMode && (
                <Button variant="contained" color="primary" onClick={handleOpenCreateModal}>
                  Create Package
                </Button>
              )}
            </PermissionGate>
          </div>
        </PageHeader>
        <PermissionBanner scope="fulfilment" resource="packages" />
      </div>

      <Outlet
        context={{
          toast,
          setToast,
          refetchTrigger,
          onMutationSuccess: handleMutationSuccess,
          setIsEditMode,
        }}
      />

      <PackageCreateModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSuccess={handleCreateSuccess}
        onError={handleCreateError}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={handleCloseToast}
          duration={5000}
        />
      )}
    </div>
  );
}

export default PackagesLayout;
